'use client';

import {default as Image} from '@/components/global/images/resized-cdn-image';
import { redirect, useParams, useRouter } from 'next/navigation';
import React, { useState, useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';

import { StrapiCountries } from '@/apis/strapi';
import {
  buildAlphabets,
  buildCount,
  buildCountries,
  buildCountry,
  buildPath,
  buildState,
  buildStep,
  buildStop
} from '@/app/builders';
import { doTranslation } from '@/app/i18n/client';
import { renderAlphabets, renderBoxes, renderCountry, renderState } from '@/app/renderer';
import Button from '@/components/global/buttons/button';
import ButtonTertiary from '@/components/global/buttons/button-tertiary';
import { getLocations } from '@/context/trip-search-context';

import styles from '@/components/global/schedule-selection/schedule-selection.module.scss';

/*
 * Step 1: /
 *         /origin_country
 * Step 2: /origin_country-origin_state
 * Step 3: /origin_country-origin_state/origin_stop
 *         /origin_country-origin_state/origin_stop/destination_country
 * Step 4: /origin_country-origin_state/origin_stop/destination_country-destination_state
 * Step 5: /origin_country-origin_state/origin_stop/destination_country-destination_state/destination_stop
 */

interface Props extends React.AllHTMLAttributes<HTMLDivElement> {
  originCountry: string;
  originState: string;
  originStop: string;
  destinationCountry: string;
  destinationState: string;
  destinationStop: string;
  testLocations?: StrapiCountries;
}

interface ExtendedProps extends Props {
  locations: StrapiCountries;
}

const ScheduleSelection = (props: ExtendedProps): React.JSX.Element => {
  const { lng } = useParams() as { lng: string };

  const alphabetButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const countrySelectRef = useRef(null);

  const { push } = useRouter();

  const [originCountrySlug] = useState(props.originCountry);
  const [originStateSlug, setOriginStateSlug] = useState(props.originState);
  const [originStopSlug, setOriginStopSlug] = useState(props.originStop);
  const [destinationCountrySlug] = useState(props.destinationCountry);
  const [destinationStateSlug, setDestinationStateSlug] = useState(props.destinationState);
  const [destinationStopSlug, setDestinationStopSlug] = useState(props.destinationStop);
  const [selectedState, setSelectedState] = useState('');
  const ctaRef = useRef<HTMLDivElement | null>(null);
  const step = useRef(1);

  const [alphabet, setAlphabet] = useState('ALL');
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const [initialRender, setInitialRender] = useState(true);

  const { t } = doTranslation(lng);

  useEffect(() => {
    const anchor = window.location.hash.slice(1);
    if (anchor) {
      const targetElement = document.getElementById(anchor);
      if (targetElement) {
        if (anchor === 'country') {
          if (countrySelectRef.current) {
            // @ts-ignore
            setTimeout(() => countrySelectRef.current?.focus());
          }
        } else if (anchor === 'depart-date') {
          targetElement?.focus();
        } else {
          targetElement.scrollIntoView();
        }
      }
    }
  }, []);

  useEffect(() => {
    // Focus and scroll to CTA when it's enabled, as user has made their selection
    if (ctaRef && ctaRef.current) {
      ctaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      ctaRef.current.focus();
    }
  });

  const formatOptionLabel = (props: any) => {
    return (
      <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'row', gap: '12px' }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <Image
          alt={t('flag')}
          height={18}
          src={`/assets/images/global/flags/${props.value}.svg`}
          width={24}
          aria-hidden="true"
        />
        <div id="locations-country-value">{props.label}</div>
      </div>
    );
  };

  const renderStep1 = (): React.JSX.Element => {
    const countries = buildCountries(props.locations);

    const originCountry = buildCountry(countries, originCountrySlug);
    if (!originCountry) {
      redirect(buildPath('/schedules', lng, '', '', '', '', '', '', {}));
    }

    const alphabets = buildAlphabets(originCountry.states.map((state: any) => state.alphabet) || []);

    return (
      <>
        <div className={styles.card}>
          <div className={styles.selectors}>
            <div className={styles.fields}>
              {renderCountry(
                countries,
                originCountry,
                styles,
                'schedules',
                countrySelectRef,
                lng,
                (selectedOption: any) =>
                  push(buildPath('/schedules', lng, selectedOption.value, '', '', '', '', '', {})),
                formatOptionLabel
              )}
            </div>
          </div>
          <div className={styles.block}>
            <div className={styles.instructions}>
              <h3 className={styles.title} id="clear-selection">
                {originCountry.value == 'ca' ? t('Select Origin Province') : t('Select Origin State')}
              </h3>
              <p className={styles.subtitle}>
                {originCountry.value == 'ca'
                  ? t(
                      'Use arrow keys to begin your search by selecting the first letter of the province from which you want to begin your trip.'
                    )
                  : t(
                      'Use arrow keys to begin your search by selecting the first letter of the state from which you want to begin your trip.'
                    )}
              </p>
            </div>
            {renderAlphabets(
              originCountry.value == 'ca' ? t('Filter origin province by letter') : t('Filter origin state by letter'),
              originCountry.value == 'ca'
                ? t('Filter Origin Province by first letter')
                : t('Filter Origin State by first letter'),
              originCountry.value == 'ca'
                ? t(
                    'Use the down and up arrow keys to navigate to the next letter, press enter to filter origin provinces by the current letter, press TAB to skip to the filtered list of provinces.'
                  )
                : t(
                    'Use the down and up arrow keys to navigate to the next letter, press enter to filter origin states by the current letter, press TAB to skip to the filtered list of states.'
                  ),
              alphabets,
              alphabet,
              alphabetButtonRefs,
              focusedIndex,
              setFocusedIndex,
              initialRender,
              setInitialRender,
              setAlphabet,
              styles,
              lng
            )}
            <div className={styles.status}>
              <strong>{alphabet == 'ALL' ? t('ALL') : alphabet}</strong> {t('chosen')}. {t('Showing')}{' '}
              <strong>{buildCount(originCountry.states, alphabet)}</strong> {t('results')}.
              <span className="sr-only">{t('Press the tab key to navigate to the list below.')}</span>
            </div>
            <div className="sr-only" id="selection-instructions">
              {originCountry.value == 'ca'
                ? t(
                    'Selecting a province from the list will move focus to the bottom of the page where you can confirm your origin province selection and click Select Origin City to go to the next step, or you can click Clear Selection to choose a different origin province. When you find the state you want, use spacebar or enter to select.'
                  )
                : t(
                    'Selecting a state from the list will move focus to the bottom of the page where you can confirm your origin state selection and click Select Origin City to go to the next step, or you can click Clear Selection to choose a different origin state. When you find the state you want, use spacebar or enter to select.'
                  )}
            </div>
            {renderBoxes(
              alphabets,
              alphabet,
              alphabetButtonRefs,
              originCountry.value == 'ca'
                ? 'Provinces beginning with {{alphabet}}'
                : 'States beginning with {{alphabet}}',
              originCountry.value == 'ca' ? 'origin-provinces-filter-{{alphabet}}' : 'origin-state-filter-{{alphabet}}',
              originCountry.states,
              originStateSlug,
              focusedIndex,
              styles,
              lng,
              (value) => setOriginStateSlug(value)
            )}
          </div>
          <div className={styles.buttons}>
            {originStateSlug === '' ? (
              <Button
                disabled
                primary
                id='gtm-locations_select_origin_city'>
                {t('Select Origin City')}
              </Button>
            ) : (
              <>
                {/* eslint-disable-next-line */}
                <div aria-live="polite" className={styles.status} ref={ctaRef} tabIndex={0}>
                  <strong>{originCountry.states.find((s: any) => s.value === originStateSlug)?.label}</strong>{' '}
                  {originCountry.value == 'ca' ? (
                    <Trans>
                      is selected as <strong>Origin Province</strong>
                    </Trans>
                  ) : (
                    <Trans>
                      is selected as <strong>Origin State</strong>
                    </Trans>
                  )}
                </div>
                <Button
                  primary
                  id='gtm-locations_select_origin_city'
                  onClick={() =>
                    (window.location.href =
                      buildPath('/schedules', lng, originCountrySlug, originStateSlug, '', '', '', '', {}) +
                      '#clear-selection')
                  }
                >
                  {t('Select Origin City')}
                </Button>
                <ButtonTertiary
                  dark
                  onClick={() => {
                    setOriginStateSlug('');
                    document.getElementById('clear-selection')?.scrollIntoView();
                  }}
                >
                  {t('Clear Selection')}
                  <span className="sr-only">
                    {t(
                      'For your convenience, clicking this link to clear your current selection will move your keyboard focus to the top of the locations list so you can easily select again.'
                    )}
                  </span>
                </ButtonTertiary>
              </>
            )}
          </div>
        </div>
        <div className={styles.buttons}>
          <Button
            disabled
            primary
            id='gtm-locations_select_destination_state'>
            {t('Select Destination State')}
          </Button>
        </div>
      </>
    );
  };

  const renderStep2 = (): React.JSX.Element => {
    const countries = buildCountries(props.locations);

    const originCountry = buildCountry(countries, originCountrySlug);
    if (!originCountry) {
      redirect(buildPath('/schedules', lng, '', '', '', '', '', '', {}));
    }

    const originState = buildState(originCountry, originStateSlug);
    if (!originState) {
      redirect(buildPath('/schedules', lng, originCountrySlug, '', '', '', '', '', {}));
    }
    if (!selectedState) {
      setSelectedState(originState.label);
    }

    const alphabets = buildAlphabets(originState.stops.map((s: any) => s.alphabet) || []);

    return (
      <>
        <div className={styles.card}>
          <div className={styles.selectors}>
            <div className={styles.fields}>
              {renderCountry(
                countries,
                originCountry,
                styles,
                'schedules',
                countrySelectRef,
                lng,
                (selectedOption: any) =>
                  push(buildPath('/schedules', lng, selectedOption.value, '', '', '', '', '', {})),
                formatOptionLabel
              )}
              {renderState(originCountry, originState, styles, 'schedules', lng, (selectedOption: any) =>
                push(buildPath('/schedules', lng, originCountrySlug, selectedOption.value, '', '', '', '', {}))
              )}
            </div>
            <ButtonTertiary
              dark
              onClick={() =>
                (window.location.href =
                  buildPath('/schedules', lng, originCountrySlug, '', '', '', '', '', {}) + '#select-origin-state')
              }
            >
              {t('Clear Selection')}
            </ButtonTertiary>
          </div>
          <div className={styles.block}>
            <div className={styles.instructions}>
              <h3 className={styles.title} id="clear-selection">
                {t('Select Origin City')}
              </h3>
              <p className={styles.subtitle}>
                {t(
                  'Use arrow keys to begin your search by selecting the first letter of the city in {{ state }} from which you want to begin your trip.',
                  { state: originState.label }
                )}
              </p>
            </div>
            {renderAlphabets(
              t('Filter origin city by letter'),
              t('Filter Origin City by first letter'),
              t(
                'Use the down and up arrow keys to navigate to the next letter, press enter to filter origin cities by the current letter, press TAB to skip to the filtered list of origin cities.'
              ),
              alphabets,
              alphabet,
              alphabetButtonRefs,
              focusedIndex,
              setFocusedIndex,
              initialRender,
              setInitialRender,
              setAlphabet,
              styles,
              lng
            )}
            <div className={styles.status}>
              <strong>{alphabet == 'ALL' ? t('ALL') : alphabet}</strong> {t('chosen')}. {t('Showing')}{' '}
              <strong>{buildCount(originState.stops, alphabet)}</strong> {t('results')}.
              <span className="sr-only">{t('Press the tab key to navigate to the list below.')}</span>
            </div>
            <div className="sr-only" id="selection-instructions">
              {t(
                'Selecting a city from the list will move focus to the bottom of the page where you can confirm your city selection and click Select Destination City to go to the next step, or you can click Clear Selection to choose a different city. When you find the city you want, use spacebar or enter to select.'
              )}
            </div>
            {renderBoxes(
              alphabets,
              alphabet,
              alphabetButtonRefs,
              'Cities beginning with {{alphabet}}',
              'origin-city-filter-{{alphabet}}',
              originState.stops,
              originStopSlug,
              focusedIndex,
              styles,
              lng,
              (value) => setOriginStopSlug(value)
            )}
          </div>
          <div className={styles.buttons}>
            {originStopSlug === '' ? (
              <>
                <div aria-live="polite" className={styles.status}>
                  <strong>{originState.label}</strong>{' '}
                  {originCountry.value == 'ca' ? (
                    <Trans>
                      is selected as <strong>Origin Province</strong>
                    </Trans>
                  ) : (
                    <Trans>
                      is selected as <strong>Origin State</strong>
                    </Trans>
                  )}
                </div>
                <ButtonTertiary
                  dark
                  onClick={() =>
                    (window.location.href =
                      buildPath('/schedules', lng, originCountrySlug, '', '', '', '', '', {}) + '#select-origin-state')
                  }
                >
                  {t('Clear Selection')}
                </ButtonTertiary>
              </>
            ) : (
              <>
                <div
                  aria-live="polite"
                  className={styles.status}
                  ref={originStopSlug === '' ? null : ctaRef}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  tabIndex={0}
                >
                  <strong>{originState.stops.find((s: any) => s.value === originStopSlug)?.label}</strong>{' '}
                  <Trans>
                    is selected as <strong>Origin City</strong>
                  </Trans>
                </div>
                <ButtonTertiary
                  dark
                  onClick={() => {
                    setOriginStopSlug('');
                    document.getElementById('clear-selection')?.scrollIntoView();
                  }}
                >
                  {t('Clear Selection')}
                  <span className="sr-only">
                    {t(
                      'For your convenience, clicking this link to clear your current selection will move your keyboard focus to the top of the locations list so you can easily select again.'
                    )}
                  </span>
                </ButtonTertiary>
              </>
            )}
          </div>
        </div>
        <div className={styles.buttons}>
          <Button
            disabled={originStopSlug === ''}
            onClick={() =>
              (window.location.href =
                buildPath('/schedules', lng, originCountrySlug, originStateSlug, originStopSlug, '', '', '', {}) +
                '#country')
            }
            primary
            id='gtm-locations_select_destination_state'
          >
            {t('Select Destination State')}
          </Button>
        </div>
      </>
    );
  };

  const renderStep3 = (): React.JSX.Element => {
    const countries = buildCountries(props.locations);

    const originCountry = buildCountry(countries, originCountrySlug);
    if (!originCountry) {
      redirect(buildPath('/schedules', lng, '', '', '', '', '', '', {}));
    }

    const originState = buildState(originCountry, originStateSlug);
    if (!originState) {
      redirect(buildPath('/schedules', lng, originCountrySlug, '', '', '', '', '', {}));
    }

    const originStop = buildStop(originState, originStopSlug);
    if (!originStop) {
      redirect(buildPath('/schedules', lng, originCountrySlug, originStateSlug, '', '', '', '', {}));
    }

    const destinationCountry = buildCountry(countries, destinationCountrySlug);
    if (!destinationCountry) {
      redirect(buildPath('/schedules', lng, originCountrySlug, originStateSlug, originStopSlug, '', '', '', {}));
    }

    const alphabets = buildAlphabets(destinationCountry.states.map((state: any) => state.alphabet) || []);

    return (
      <>
        <div className={styles.notice}>
          <div>
            <strong>{originState.stops.find((s: any) => s.value === originStopSlug)?.label}</strong>{' '}
            <Trans>
              is selected as <strong>Origin City</strong>
            </Trans>
          </div>
          <ButtonTertiary
            dark
            onClick={() =>
              (window.location.href = buildPath('/schedules', lng, '', '', '', '', '', '', {}) + '#select-origin-state')
            }
          >
            {t('Clear Selection')}
          </ButtonTertiary>
        </div>
        <div className={styles.card}>
          <div className={styles.selectors}>
            <div className={styles.fields}>
              {renderCountry(
                countries,
                destinationCountry,
                styles,
                'schedules',
                countrySelectRef,
                lng,
                (selectedOption: any) =>
                  push(
                    buildPath(
                      '/schedules',
                      lng,
                      originCountrySlug,
                      originStateSlug,
                      originStopSlug,
                      selectedOption.value,
                      '',
                      '',
                      {}
                    )
                  ),
                formatOptionLabel
              )}
            </div>
          </div>
          <div className={styles.block}>
            <div className={styles.instructions}>
              <h3 className={styles.title} id="clear-selection">
                {destinationCountry.value == 'ca' ? t('Select Destination Province') : t('Select Destination State')}
              </h3>
              <p className={styles.subtitle}>
                {destinationCountry.value == 'ca'
                  ? t(
                      'Use arrow keys to begin your search by selecting the first letter of the province from which you want to end your trip.'
                    )
                  : t(
                      'Use arrow keys to begin your search by selecting the first letter of the state from which you want to end your trip.'
                    )}
              </p>
            </div>
            {renderAlphabets(
              destinationCountry.value == 'ca'
                ? t('Filter destination province by letter')
                : t('Filter destination state by letter'),
              destinationCountry.value == 'ca'
                ? t('Filter Destination Province by first letter')
                : t('Filter Destination State by first letter'),
              originCountry.value == 'ca'
                ? t(
                    'Use the arrow keys to navigate to the next letter, press enter to filter destination provinces by the current letter, press TAB to skip to the filtered list of destination provinces.'
                  )
                : t(
                    'Use the arrow keys to navigate to the next letter, press enter to filter destination states by the current letter, press TAB to skip to the filtered list of destination states.'
                  ),
              alphabets,
              alphabet,
              alphabetButtonRefs,
              focusedIndex,
              setFocusedIndex,
              initialRender,
              setInitialRender,
              setAlphabet,
              styles,
              lng
            )}
            <div className={styles.status}>
              <strong>{alphabet == 'ALL' ? t('ALL') : alphabet}</strong> {t('chosen')}. {t('Showing')}{' '}
              <strong>{buildCount(destinationCountry.states, alphabet)}</strong> {t('results')}.
              <span className="sr-only">{t('Press the tab key to navigate to the list below.')}</span>
            </div>
            <div className="sr-only" id="selection-instructions">
              {destinationCountry.value == 'ca'
                ? t(
                    'Selecting a province from the list will move focus to the bottom of the page where you can confirm your destination province selection and click Select Destination City to go to the next step, or you can click Clear Selection to choose a different destination province. When you find the province you want, use spacebar or enter to select.'
                  )
                : t(
                    'Selecting a state from the list will move focus to the bottom of the page where you can confirm your destination state selection and click Select Destination City to go to the next step, or you can click Clear Selection to choose a different destination state. When you find the state you want, use spacebar or enter to select.'
                  )}
            </div>
            {renderBoxes(
              alphabets,
              alphabet,
              alphabetButtonRefs,
              destinationCountry.value == 'ca'
                ? 'Provinces beginning with {{alphabet}}'
                : 'States beginning with {{alphabet}}',
              destinationCountry.value == 'ca'
                ? 'destination-provinces-filter-{{alphabet}}'
                : 'destination-state-filter-{{alphabet}}',
              destinationCountry.states,
              destinationStateSlug,
              focusedIndex,
              styles,
              lng,
              (value) => setDestinationStateSlug(value)
            )}
          </div>
          <div className={styles.buttons}>
            {destinationStateSlug === '' ? (
              <Button
                disabled
                primary
                id='gtm-locations_select_destination_city'>
                {t('Select Destination City')}
              </Button>
            ) : (
              <>
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                <div aria-live="polite" className={styles.status} ref={ctaRef} tabIndex={0}>
                  <strong>{destinationCountry.states.find((s: any) => s.value === destinationStateSlug)?.label}</strong>{' '}
                  {destinationCountry.value == 'ca' ? (
                    <Trans>
                      is selected as <strong>Destination Province</strong>
                    </Trans>
                  ) : (
                    <Trans>
                      is selected as <strong>Destination State</strong>
                    </Trans>
                  )}
                </div>
                <Button
                  primary
                  id='gtm-locations_select_destination_city'
                  onClick={() =>
                    (window.location.href =
                      buildPath(
                        '/schedules',
                        lng,
                        originCountrySlug,
                        originStateSlug,
                        originStopSlug,
                        destinationCountrySlug,
                        destinationStateSlug,
                        '',
                        {}
                      ) + '#clear-selection')
                  }
                >
                  {t('Select Destination City')}
                </Button>
                <ButtonTertiary
                  dark
                  onClick={() => {
                    setDestinationStateSlug('');
                    document.getElementById('clear-selection')?.scrollIntoView();
                  }}
                >
                  {t('Clear Selection')}
                  <span className="sr-only">
                    {t(
                      'For your convenience, clicking this link to clear your current selection will move your keyboard focus to the top of the locations list so you can easily select again.'
                    )}
                  </span>
                </ButtonTertiary>
              </>
            )}
          </div>
        </div>
        <div className={styles.buttons}>
          <Button
            disabled
            primary
            id='gtm-locations_choose_depart_date'>
            {t('Choose Depart Date')}
          </Button>
        </div>
      </>
    );
  };

  const renderStep4 = (): React.JSX.Element => {
    const countries = buildCountries(props.locations);

    const originCountry = buildCountry(countries, originCountrySlug);
    if (!originCountry) {
      redirect(buildPath('/schedules', lng, '', '', '', '', '', '', {}));
    }

    const originState = buildState(originCountry, originStateSlug);
    if (!originState) {
      redirect(buildPath('/schedules', lng, originCountrySlug, '', '', '', '', '', {}));
    }

    const originStop = buildStop(originState, originStopSlug);
    if (!originStop) {
      redirect(buildPath('/schedules', lng, originCountrySlug, originStateSlug, '', '', '', '', {}));
    }

    const destinationCountry = buildCountry(countries, destinationCountrySlug);
    if (!destinationCountry) {
      redirect(buildPath('/schedules', lng, originCountrySlug, originStateSlug, originStopSlug, '', '', '', {}));
    }

    const destinationState = buildState(destinationCountry, destinationStateSlug);
    if (!destinationState) {
      redirect(
        buildPath(
          '/schedules',
          lng,
          originCountrySlug,
          originStateSlug,
          originStopSlug,
          destinationCountrySlug,
          '',
          '',
          {}
        )
      );
    }
    if (!selectedState) {
      setSelectedState(destinationState.label);
    }

    const alphabets = buildAlphabets(destinationState.stops.map((state: any) => state.alphabet) || []);

    return (
      <>
        <div className={styles.notice}>
          <div>
            <strong>{originState.stops.find((s: any) => s.value === originStopSlug)?.label}</strong>{' '}
            <Trans>
              is selected as <strong>Origin City</strong>
            </Trans>
          </div>
          <ButtonTertiary
            dark
            onClick={() =>
              (window.location.href = buildPath('/schedules', lng, '', '', '', '', '', '', {}) + '#select-origin-state')
            }
          >
            {t('Clear Selection')}
          </ButtonTertiary>
        </div>
        <div className={styles.card}>
          <div className={styles.selectors}>
            <div className={styles.fields}>
              {renderCountry(
                countries,
                destinationCountry,
                styles,
                'schedules',
                countrySelectRef,
                lng,
                (selectedOption: any) =>
                  push(
                    buildPath(
                      '/schedules',
                      lng,
                      originCountrySlug,
                      originStateSlug,
                      originStopSlug,
                      selectedOption.value,
                      '',
                      '',
                      {}
                    )
                  ),
                formatOptionLabel
              )}
              {renderState(destinationCountry, destinationState, styles, 'schedules', lng, (selectedOption: any) =>
                push(
                  buildPath(
                    '/schedules',
                    lng,
                    originCountrySlug,
                    originStateSlug,
                    originStopSlug,
                    destinationCountrySlug,
                    selectedOption.value,
                    '',
                    {}
                  )
                )
              )}
            </div>
            <ButtonTertiary
              dark
              onClick={() =>
                (window.location.href =
                  buildPath('/schedules', lng, originCountrySlug, originStateSlug, originStopSlug, '', '', '', {}) +
                  '#select-destination-state')
              }
            >
              {t('Clear Selection')}
            </ButtonTertiary>
          </div>
          <div className={styles.block}>
            <div className={styles.instructions}>
              <h3 className={styles.title} id="clear-selection">
                {t('Select Destination City')}
              </h3>
              <p className={styles.subtitle}>
                {t(
                  'Use arrow keys to begin your search by selecting the first letter of the city in {{ state }} from which you want to end your trip.',
                  { state: destinationState.label }
                )}
              </p>
            </div>
            {renderAlphabets(
              t('Filter destination city by letter'),
              t('Filter Destination City by first letter'),
              t(
                'Use the down and up arrow keys to navigate to the next letter, press enter to filter destination cities by the current letter, press TAB to skip to the filtered list of destination cities.'
              ),
              alphabets,
              alphabet,
              alphabetButtonRefs,
              focusedIndex,
              setFocusedIndex,
              initialRender,
              setInitialRender,
              setAlphabet,
              styles,
              lng
            )}
            <div className={styles.status}>
              <strong>{alphabet == 'ALL' ? t('ALL') : alphabet}</strong> {t('chosen')}. {t('Showing')}{' '}
              <strong>{buildCount(destinationState.stops, alphabet)}</strong> {t('results')}.
              <span className="sr-only">{t('Press the tab key to navigate to the list below.')}</span>
            </div>
            <div className="sr-only" id="selection-instructions">
              {t(
                'Selecting a city from the list will move focus to the bottom of the page where you can confirm your city selection and click Select Destination City to go to the next step, or you can click Clear Selection to choose a different city. When you find the city you want, use spacebar or enter to select.'
              )}
            </div>
            {renderBoxes(
              alphabets,
              alphabet,
              alphabetButtonRefs,
              'Cities beginning with {{alphabet}}',
              'destination-city-filter-{{alphabet}}',
              destinationState.stops,
              destinationStopSlug,
              focusedIndex,
              styles,
              lng,
              (value) => setDestinationStopSlug(value)
            )}
          </div>
          <div className={styles.buttons}>
            {destinationStopSlug === '' ? (
              <>
                <div aria-live="polite" className={styles.status}>
                  <strong>{destinationState.label}</strong>{' '}
                  {destinationCountry.value == 'ca' ? (
                    <Trans>
                      is selected as <strong>Destination Province</strong>
                    </Trans>
                  ) : (
                    <Trans>
                      is selected as <strong>Destination State</strong>
                    </Trans>
                  )}
                </div>
                <ButtonTertiary
                  dark
                  onClick={() =>
                    (window.location.href =
                      buildPath(
                        '/schedules',
                        lng,
                        originCountrySlug,
                        originStateSlug,
                        originStopSlug,
                        destinationCountrySlug,
                        '',
                        '',
                        {}
                      ) + '#select-destination-state')
                  }
                >
                  {t('Clear Selection')}
                </ButtonTertiary>
              </>
            ) : (
              <>
                <div aria-live="polite" className={styles.status}>
                  <strong>{destinationState.stops.find((s: any) => s.value === destinationStopSlug)?.label}</strong>{' '}
                  <Trans>
                    is selected as <strong>Destination City</strong>
                  </Trans>
                </div>
                <ButtonTertiary
                  dark
                  onClick={() => {
                    setDestinationStopSlug('');
                    document.getElementById('clear-selection')?.scrollIntoView();
                  }}
                >
                  {t('Clear Selection')}
                  <span className="sr-only">
                    {t(
                      'For your convenience, clicking this link to clear your current selection will move your keyboard focus to the top of the locations list so you can easily select again.'
                    )}
                  </span>
                </ButtonTertiary>
              </>
            )}
          </div>
        </div>
        <div className={styles.buttons}>
          {destinationStopSlug === '' ? (
            <Button
              disabled
              primary
              id='gtm-locations_choose_depart_date'>
              {t('Choose Depart Date')}
            </Button>
          ) : (
            <>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
              <div aria-live="polite" className={styles.status} ref={ctaRef} tabIndex={0}>
                <strong>{originState.stops.find((s: any) => s.value === originStopSlug)?.label}</strong>{' '}
                <Trans>
                  is selected as <strong>Origin City</strong>
                </Trans>{' '}
                {t('and')}{' '}
                <strong>{destinationState.stops.find((s: any) => s.value === destinationStopSlug)?.label}</strong>{' '}
                <Trans>
                  is selected as <strong>Destination City</strong>
                </Trans>
              </div>
              <Button
                onClick={() =>
                  (window.location.href =
                    buildPath(
                      '/schedules',
                      lng,
                      originCountrySlug,
                      originStateSlug,
                      originStopSlug,
                      destinationCountrySlug,
                      destinationStateSlug,
                      destinationStopSlug,
                      {}
                    ) + '#depart-date')
                }
                primary
                id='gtm-locations_choose_depart_date'
              >
                {t('Choose Depart Date')}
              </Button>
              <ButtonTertiary
                dark
                onClick={() =>
                  (window.location.href =
                    buildPath('/schedules', lng, '', '', '', '', '', '', {}) + '#select-origin-state')
                }
              >
                {t('Clear Selection')}
              </ButtonTertiary>
            </>
          )}
        </div>
      </>
    );
  };

  const renderStep5 = (): React.JSX.Element => {
    const countries = buildCountries(props.locations);

    const originCountry = buildCountry(countries, originCountrySlug);
    if (!originCountry) {
      redirect(buildPath('/schedules', lng, '', '', '', '', '', '', {}));
    }

    const originState = buildState(originCountry, originStateSlug);
    if (!originState) {
      redirect(buildPath('/schedules', lng, originCountrySlug, '', '', '', '', '', {}));
    }

    const originStop = buildStop(originState, originStopSlug);
    if (!originStop) {
      redirect(buildPath('/schedules', lng, originCountrySlug, originStateSlug, '', '', '', '', {}));
    }

    const destinationCountry = buildCountry(countries, destinationCountrySlug);
    if (!destinationCountry) {
      redirect(buildPath('/schedules', lng, originCountrySlug, originStateSlug, originStopSlug, '', '', '', {}));
    }

    const destinationState = buildState(destinationCountry, destinationStateSlug);
    if (!destinationState) {
      redirect(
        buildPath(
          '/schedules',
          lng,
          originCountrySlug,
          originStateSlug,
          originStopSlug,
          destinationCountrySlug,
          '',
          '',
          {}
        )
      );
    }

    const destinationStop = buildStop(destinationState, destinationStopSlug);
    if (!destinationStop) {
      redirect(
        buildPath(
          '/schedules',
          lng,
          originCountrySlug,
          originStateSlug,
          originStopSlug,
          destinationCountrySlug,
          destinationStateSlug,
          '',
          {}
        )
      );
    }

    return (
      <>
        <div className={styles.notice}>
          <div>
            <strong>{originState.stops.find((s: any) => s.value === originStopSlug)?.label}</strong>{' '}
            <Trans>
              is selected as <strong>Origin City</strong>
            </Trans>
          </div>
          <ButtonTertiary
            dark
            onClick={() =>
              (window.location.href = buildPath('/schedules', lng, '', '', '', '', '', '', {}) + '#select-origin-state')
            }
          >
            {t('Clear Selection')}
          </ButtonTertiary>
        </div>
        <div className={styles.notice}>
          <div>
            <strong>{destinationState.stops.find((s: any) => s.value === destinationStopSlug)?.label}</strong>{' '}
            <Trans>
              is selected as <strong>Destination City</strong>
            </Trans>
          </div>
          <ButtonTertiary
            dark
            onClick={() =>
              (window.location.href =
                buildPath('/schedules', lng, originCountrySlug, originStateSlug, originStopSlug, '', '', '', {}) +
                '#select-destination-state')
            }
          >
            {t('Clear Selection')}
          </ButtonTertiary>
        </div>
      </>
    );
  };

  step.current = buildStep(
    props.originCountry,
    props.originState,
    props.originStop,
    props.destinationCountry,
    props.destinationState,
    props.destinationStop
  );

  return (
    <div className={styles.schedules}>
      <div className={styles.description}>
        <h2 className={styles.title}>{t('You may also search for bus schedules using our bus trip locations list')}</h2>
        <p className={styles.subtitle}>
          {step.current === 2 || step.current === 4
            ? t(
                'This section provides an alphabetical list of all cities in {{ state }} from which our buses depart.',
                {
                  state: selectedState
                }
              )
            : t('This section provides an alphabetical list of all locations from which our buses depart.')}
          <br />
          {t('Begin your search by choosing a country, state and city.')}
        </p>
      </div>
      {step.current === 1 && renderStep1()}
      {step.current === 2 && renderStep2()}
      {step.current === 3 && renderStep3()}
      {step.current === 4 && renderStep4()}
      {step.current === 5 && renderStep5()}
    </div>
  );
};

const ScheduleSelectionWrapper = (props: Props): React.JSX.Element => {
  const [locationsData, setLocationsData] = useState<StrapiCountries | null>(null);

  useEffect(() => {
    if (props.testLocations) {
      setLocationsData(props.testLocations);
      return;
    }
    (async () => {
      const data = await getLocations();
      if (data) {
        setLocationsData(data);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return locationsData ? <ScheduleSelection locations={locationsData} {...props} /> : <></>;
};

export default ScheduleSelectionWrapper;
